<script>
    import { onMount } from 'svelte';

    let groupedRequests = {};
    let loading = true;

    // map of requestId → boolean (expanded?)
    let expandedMap = {};

    async function handleFetchRequests() {
      const token = localStorage.getItem('jwtToken');
      try {
        const res = await fetch(
          '/studentplayground/api/teacher/class-registration-requests/',
          {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type':  'application/json'
            }
          }
        );
        if (!res.ok) {
            const errorData = await res.json();
            throw new Error(errorData.error || res.statusText);
        }
        groupedRequests = await res.json();
      } catch (err) {
        console.error('Error fetching requests:', err);
        alert(`Klaida įkeliant užklausas: ${err.message}`);
      } finally {
        loading = false;
      }
    }

    onMount(handleFetchRequests);

    // toggle expanded state for one card
    function toggleExpand(id) {
      expandedMap[id] = !expandedMap[id];
      // reassign to trigger Svelte reactivity
      expandedMap = { ...expandedMap };
    }

    function formatDateTime(iso) {
      if (!iso) return '-';
      const d = new Date(iso);
      // Format in Lithuania timezone
      return `${d.toLocaleDateString('lt-LT', { timeZone: 'Europe/Vilnius' })} at ${d.toLocaleTimeString('lt-LT', { 
        timeZone: 'Europe/Vilnius',
        hour: '2-digit', 
        minute: '2-digit' 
      })}`;
    }

    function getStudentName(student) {
      return student || 'Nežinomas studentas';
    }

    function getSubjectDisplayName(subject) {
      const subjectNames = {
        "Math": "Matematika",
        "English": "Anglų kalba", 
        "Lithuanian": "Lietuvių kalba",
        "History": "Istorija",
        "Chemistry": "Chemija",
        "Physics": "Fizika",
        "Biology": "Biologija",
        "IT": "Informacinės technologijos"
      };
      return subjectNames[subject] || subject;
    }

    function getGradeDisplayName(grade) {
      if (grade === 'university') {
        return 'Universitetas';
      }
      return `${grade} klasė`;
    }

    function formatRequestedTimes(requestedTimes) {
      if (!requestedTimes || requestedTimes.length === 0) {
        return 'Laikas nenurodytas';
      }
      return requestedTimes.map(time => {
        if (!time) return 'Neteisingas laikas';
        const d = new Date(time);
        // Format in Lithuania timezone
        return `${d.toLocaleDateString('lt-LT', { timeZone: 'Europe/Vilnius' })} ${d.toLocaleTimeString('lt-LT', { 
          timeZone: 'Europe/Vilnius',
          hour: '2-digit', 
          minute: '2-digit' 
        })}`;
      }).join(', ');
    }

    function formatRequestedTime(time) {
      if (!time) return 'Neteisingas laikas';
      const d = new Date(time);
      // Format in Lithuania timezone
      const date = d.toLocaleDateString('lt-LT', { 
        timeZone: 'Europe/Vilnius',
        month: 'short', 
        day: 'numeric',
        year: d.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
      });
      const timeStr = d.toLocaleTimeString('lt-LT', { 
        timeZone: 'Europe/Vilnius',
        hour: '2-digit', 
        minute: '2-digit' 
      });
      return `${date} ${timeStr}`;
    }

    // Function to handle teacher accept action
    async function handleAcceptRequest(requestId) {
      const token = localStorage.getItem('jwtToken');
      
      try {
        const response = await fetch(
          '/studentplayground/api/teacher/accept-registration/',
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              request_id: requestId
            })
          }
        );

        if (response.ok) {
          const result = await response.json();
          alert(`Registracijos užklausa sėkmingai priimta!`);
          // Refresh the requests list
          await handleFetchRequests();
        } else {
          const error = await response.json();
          console.error('Failed to accept request:', error);
          alert(`Nepavyko priimti užklausos: ${error.error || response.statusText}`);
        }
      } catch (error) {
        console.error('Error accepting request:', error);
        alert(`Klaida priimant užklausą: ${error.message}`);
      }
    }
  </script>
  
  <style>
  /* Header styling */
  .labelDiv {
    display: flex;
    justify-content: space-around;
    margin-bottom: 2rem;
    margin-top: 2rem;
  }
  .labelDiv span {
    font-size: 1.2rem;
    font-weight: bold;
    color: #333;
  }

  /* Loading and empty states */
  .loading-message, .empty-message {
    text-align: center;
    padding: 2rem;
    color: #666;
    font-style: italic;
  }

  /* Date group styling */
  .date-group {
    margin-bottom: 2rem;
  }
  .date-header {
    font-size: 1.1rem;
    font-weight: bold;
    color: #0055aa;
    border-bottom: 2px solid #0055aa;
    padding-bottom: 0.5rem;
    margin-bottom: 1rem;
  }

  /* Request card styling */
  .request-card {
    border: 2px solid #e0e0e0;
    border-radius: 12px;
    margin-bottom: 1rem;
    background: #fff;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    transition: all 0.3s ease;
    width: 100%;
    box-sizing: border-box;
  }
  
  .request-card:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    transform: translateY(-2px);
  }

  .request-card.accepted {
    border-color: #28a745;
    background: #f8fff9;
  }

  .request-content {
    padding: 1.5rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
    width: 100%;
    box-sizing: border-box;
  }

  .student-info {
    flex: 1;
  }

  .student-name {
    margin: 0 0 0.5rem 0;
    font-size: 1.3rem;
    font-weight: 600;
    color: #333;
  }

  .class-info {
    display: flex;
    gap: 1rem;
    margin-bottom: 0.8rem;
  }

  .subject {
    background: transparent;
    color: #007bff;
    border: 2px solid #007bff;
    padding: 0.3rem 0.8rem;
    border-radius: 20px;
    font-size: 0.9rem;
    font-weight: 500;
  }

  .grade {
    background: transparent;
    color: #6c757d;
    border: 2px solid #6c757d;
    padding: 0.3rem 0.8rem;
    border-radius: 20px;
    font-size: 0.9rem;
    font-weight: 500;
  }

  .topic {
    color: #666;
    font-size: 0.95rem;
    line-height: 1.4;
    margin-top: 1.2rem;
  }

  .topic strong {
    color: #333;
  }

  .requested-times {
    margin-top: 0.8rem;
  }

  .requested-times strong {
    color: #333;
    font-size: 0.95rem;
    display: block;
    margin-bottom: 0.5rem;
  }

  .times-list {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .time-badge {
    background: #f8f9fa;
    color: #495057;
    border: 1px solid #dee2e6;
    padding: 0.25rem 0.6rem;
    border-radius: 6px;
    font-size: 0.85rem;
    font-weight: 500;
    white-space: nowrap;
  }

  .no-times {
    color: #6c757d;
    font-style: italic;
    font-size: 0.9rem;
  }

  /* Action section */
  .action-section {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 140px;
  }

  .accept-button {
    background: #28a745;
    color: white;
    border: none;
    padding: 12px 24px;
    border-radius: 8px;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    white-space: nowrap;
  }
  
  .accept-button:hover {
    background: #218838;
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(40, 167, 69, 0.3);
  }

  .accept-button:active {
    transform: translateY(0);
  }

  .accepted-indicator {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 12px 24px;
    background: #d4edda;
    color: #155724;
    border: 2px solid #28a745;
    border-radius: 8px;
    font-weight: 600;
    font-size: 1rem;
  }

  .check-icon {
    font-size: 1.2rem;
    font-weight: bold;
  }

  /* Responsive adjustments */
  @media (max-width: 768px) {
    .request-content {
      flex-direction: column;
      align-items: flex-start;
      gap: 1rem;
    }
    
    .class-info {
      flex-wrap: wrap;
      gap: 0.5rem;
    }
    
    .action-section {
      width: 100%;
      justify-content: flex-start;
    }
    
    .accept-button, .accepted-indicator {
      width: 100%;
      justify-content: center;
    }
  }
</style>
  
  <div class="labelDiv">
    <span>KLASIŲ REGISTRACIJOS UŽKLAUSOS</span>
  </div>

{#if loading}
  <div class="loading-message">Įkeliama…</div>
{:else if Object.keys(groupedRequests).length === 0}
  <div class="empty-message">Klasių registracijos užklausų nerasta.</div>
{:else}
  {#each Object.entries(groupedRequests) as [day, requests]}
    <div class="date-group">
      <h2 class="date-header">{day}</h2>

      {#each requests as req (req.id)}
        <div class="request-card" class:accepted={req.accepted_by_teacher}>
          <div class="request-content">
            <div class="student-info">
              <h4 class="student-name">{getStudentName(req.student)}</h4>
              <div class="class-info">
                <span class="subject">{getSubjectDisplayName(req.subject)}</span>
                <span class="grade">{getGradeDisplayName(req.grade)}</span>
              </div>
              
              <div class="requested-times">
                <strong>Pageidaujamas laikas:</strong>
                <div class="times-list">
                  {#each req.requested_times as time}
                    {#if time}
                      <span class="time-badge">{formatRequestedTime(time)}</span>
                    {/if}
                  {/each}
                  {#if !req.requested_times || req.requested_times.length === 0}
                    <span class="no-times">Konkretus laikas nenurodytas</span>
                  {/if}
                </div>
              </div>
              
              {#if req.topic_start}
                <div class="topic">
                  <strong>Tema:</strong> {req.topic_start}
                </div>
              {/if}
            </div>
            
            <div class="action-section">
              {#if req.accepted_by_teacher}
                <div class="accepted-indicator">
                  <span class="check-icon">✓</span>
                  <span>Priimta</span>
                </div>
              {:else}
                <button 
                  class="accept-button"
                  on:click={() => handleAcceptRequest(req.id)}
                >
                  Priimti užklausą
                </button>
              {/if}
            </div>
          </div>
        </div>
      {/each}
    </div>
  {/each}
{/if}
